import React from 'react';
import { cloneDeep, get } from 'lodash';

// local dependencies
import { MemberInfoCommunicationSettingsWellConnectService } from 'services/memberInfoCommunicationSettingsWellConnect';
import { memberInfoCommunicationSettingsWellConnect as MemberInfoCommunicationSettingsWellConnectView } from 'ui/views/widgets/memberInfoCommunicationSettingsWellConnect';
import { GLOBAL_LABELS, SERVICE_RESPONSE_STATUS, ERROR_RESPONSE_TEMPLATE, SIZES } from 'constants/common';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import * as successOverlay from 'utils/successOverlay';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { MemberInfoCommunicationSettingsWellConnect } from 'ui/containers/MemberInfoCommunicationSettingsWellConnect';
 *
 * // render
 * <MemberInfoCommunicationSettingsWellConnect propName='propValue'/>
 */
export class MemberInfoCommunicationSettingsWellConnect extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            optOutSection: {}
        };

        this.fetchOptInForm = this.fetchOptInForm.bind( this );
        this.handleActionClick = this.handleActionClick.bind( this );
        this.handleOptInFormSubmit = this.handleOptInFormSubmit.bind( this );
        this.fetchEditWellConnectPhoneForm = this.fetchEditWellConnectPhoneForm.bind( this );
        this.handleEditWellConnectPhoneSubmit = this.handleEditWellConnectPhoneSubmit.bind( this );
        this.handleOptOutActionClick = this.handleOptOutActionClick.bind( this );

        this.memberInfoCommunicationSettingsWellConnectService = new MemberInfoCommunicationSettingsWellConnectService();

        this.fetchComponentData();
    }

    /**
     * @desc Handles fetching of widget data
     */
    async fetchComponentData() {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.fetchComponentData()' );

        this.memberInfoCommunicationSettingsWellConnectService.cancel();

        const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload
        } );

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        }
    }

    /**
     * @desc Handles fetching of opt in/more details form
     */
    async fetchOptInForm( url ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.fetchOptInForm()' );

        this.memberInfoCommunicationSettingsWellConnectService.cancel();

        pageLevelPreloader.show();

        const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.fetchOptInForm( {
            url,
            data: this.props.requestPayload
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.WELL_CONNECT_OPT_IN,
                    showCloseButton: false,
                    ignoreOutsideClick: false
                },
                data: {
                    onFormSubmit: this.handleOptInFormSubmit,
                    onCancelClick: overlay.close,
                    ...data
                }
            } );
        } else {
            errorOverlay.open();
        }
    }

    /**
     * @desc Handles opt in form submission
     */
    async handleOptInFormSubmit( { formData, submitAction } ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.handleOptInFormSubmit()' );

        this.memberInfoCommunicationSettingsWellConnectService.cancel();

        overlay.close();

        pageLevelPreloader.show();

        const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.submitOptInForm( {
            url: submitAction.url,
            data: {
                context: this.props.requestPayload.context,
                data: formData
            }
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: true,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {
                successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ) } );
                this.fetchComponentData();
            } );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if ( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /**
     * @desc Handles fetching of edit well connect phone form
     */
    async fetchEditWellConnectPhoneForm( url ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.fetchEditWellConnectPhoneForm()' );

        this.memberInfoCommunicationSettingsWellConnectService.cancel();

        pageLevelPreloader.show();

        const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.fetchEditWellConnectPhoneForm( {
            url,
            data: {
                context: this.props.requestPayload.context
            }
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            overlay.open( {
                settings: {
                    modalType: OVERLAY_VIEWS.EDIT_WELL_CONNECT_PHONE,
                    showCloseButton: false,
                    ignoreOutsideClick: false
                },
                data: {
                    onFormSubmit: this.handleEditWellConnectPhoneSubmit,
                    onCancelClick: overlay.close,
                    ...data
                }
            } );
        } else {
            errorOverlay.open();
        }
    }

    /**
     * Handles edit well connect form submission
     */
    async handleEditWellConnectPhoneSubmit( { formData, saveAction } ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.handleEditWellConnectPhoneSubmit()' );

        const oldPhoneNumber = this.state.componentData.values.phoneNumber;
        const newPhoneNumber = formData.phoneNumber;

        if ( oldPhoneNumber === newPhoneNumber ) {
            errorOverlay.open( { message: getLabel( saveAction.duplicatePhoneError ), size: SIZES.SMALL } );
        } else {

            this.memberInfoCommunicationSettingsWellConnectService.cancel();

            overlay.close();

            pageLevelPreloader.show();

            const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.submitEditWellConnectPhone( {
                url: saveAction.url,
                data: {
                    context: this.props.requestPayload.context,
                    data: {
                        oldPhoneNumber,
                        newPhoneNumber
                    }
                }
            } );

            pageLevelPreloader.hide();

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                const _componentData = cloneDeep( this.state.componentData );

                // update new number in state to reflect on UI instantly
                _componentData.values = {
                    ..._componentData.values,
                    ...formData
                };

                this.setState( {
                    componentData: _componentData
                }, () => {
                    successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ) } );
                } );
            } else {
                const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

                if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
                } else {
                    errorOverlay.open();
                }
            }
        }
    }

    /**
     * @desc Handles opt out actions click
     */
    async handleOptOutActionClick( { name, action } ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.handleOptOutActionClick()' );
        
        if ( 'optOut' === name ) {
            this.setState( {
                optOutSection: action.config
            } );
        } else if ( 'cancelOptOut' === name ) {
            this.setState( {
                optOutSection: {}
            } );
        } else if ( 'submitOptOut' === name ) {
            this.memberInfoCommunicationSettingsWellConnectService.cancel();

            pageLevelPreloader.show();

            const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.submitOptOutRequest( {
                url: action.url,
                data: {
                    context: this.props.requestPayload.context,
                    data: {
                        optOut: true,
                        phoneNumber: this.state.componentData.values.phoneNumber
                    }
                }
            } );

            pageLevelPreloader.hide();

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                this.setState( {
                    isFetching: true,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    optOutSection: {}
                }, () => {
                    successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ) } );
                    this.fetchComponentData();
                } );
            } else {
                const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

                if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                    errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
                } else {
                    errorOverlay.open();
                }
            }
        }
    }

    /**
     * @desc Handles not interested action click
     */
    async handleNotInterestedActionClick( url ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.handleNotInterestedActionClick()' );

        this.memberInfoCommunicationSettingsWellConnectService.cancel();

        pageLevelPreloader.show();

        const { status, data } = await this.memberInfoCommunicationSettingsWellConnectService.submitNotInterestedRequest( {
            url,
            data: {
                context: this.props.requestPayload.context,
                data: { notInterested: true }
            }
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: true,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS
            }, () => {
                successOverlay.open( { message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ) } );
                this.fetchComponentData();
            } );
        } else {
            const template = get( data, 'template', ERROR_RESPONSE_TEMPLATE.DEFAULT );

            if( template === ERROR_RESPONSE_TEMPLATE.notification ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }
    
    /**
     * @desc Handles action clicks
     */
    handleActionClick( { name, action } ) {
        log.debug( 'MemberInfoCommunicationSettingsWellConnect.handleActionClick()' );

        if ( 'hereAction' === name || 'optIn' === name ) {
            this.fetchOptInForm( action.url );
        } else if ( 'notInterested' === name ) {
            this.handleNotInterestedActionClick( action.url );
        } else if ( 'editPhone' === name ) {
            this.fetchEditWellConnectPhoneForm( action.url );
        } else if ( 'optOut' === name || 'cancelOptOut' === name || 'submitOptOut' === name ) {
            this.handleOptOutActionClick( { name, action } );
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <MemberInfoCommunicationSettingsWellConnectView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...this.state.componentData }
                optOutSection={ this.state.optOutSection }
                onActionClick={ this.handleActionClick }
            />
        );
    }
}

// set display name
MemberInfoCommunicationSettingsWellConnect.displayName = 'MemberInfoCommunicationSettingsWellConnect';

// set default props
MemberInfoCommunicationSettingsWellConnect.defaultProps = {};

// prop types
MemberInfoCommunicationSettingsWellConnect.propTypes = {};
